import { useState, useEffect, useRef } from 'react';
import useLogActivityAccess from '../hooks/useLogActivityAccess';
import './Dialogos.css';

const NIVEL_LABELS = { beginner: 'Iniciante', intermediate: 'Intermediário', advanced: 'Avançado' };
const PAR_LABELS   = { hm: 'Homem & Mulher', mm: 'Homem & Homem', ff: 'Mulher & Mulher' };

export default function Dialogos() {
  useLogActivityAccess('dialogos');

  const [lista,          setLista]          = useState([]);
  const [loadingLista,   setLoadingLista]   = useState(true);
  const [selected,       setSelected]       = useState(null);
  const [loadingDialogo, setLoadingDialogo] = useState(false);
  const [playingIdx,     setPlayingIdx]     = useState(null);
  const [isPlayingAll,   setIsPlayingAll]   = useState(false);
  const [showTranslation,setShowTranslation]= useState(false);
  const [speed,          setSpeed]          = useState(1);
  const [search,         setSearch]         = useState('');
  const [filterNivel,    setFilterNivel]    = useState('');
  const [filterTipo,     setFilterTipo]     = useState('');
  const [mobileView,     setMobileView]     = useState('list');

  const audioRef   = useRef(new Audio());
  const audioCache = useRef(new Map());
  const stopRef    = useRef(false);
  const lineRefs   = useRef([]);

  useEffect(() => {
    fetch('/api/dialogos', { credentials: 'include' })
      .then(r => r.json())
      .then(setLista)
      .catch(() => {})
      .finally(() => setLoadingLista(false));
  }, []);

  function stopAll() {
    stopRef.current = true;
    setIsPlayingAll(false);
    setPlayingIdx(null);
    const a = audioRef.current;
    a.pause();
    a.onended = null;
    a.onerror = null;
  }

  async function selectDialogo(id) {
    stopAll();
    setLoadingDialogo(true);
    setMobileView('dialogue');
    lineRefs.current = [];
    try {
      const res = await fetch(`/api/dialogos/${id}`, { credentials: 'include' });
      const data = await res.json();
      setSelected(data);
    } catch (e) { /* ignore */ }
    setLoadingDialogo(false);
  }

  async function playLine(idx, currentSelected) {
    const dialogo = currentSelected || selected;
    if (!dialogo || stopRef.current) return;

    const linha    = dialogo.linhas[idx];
    const voz      = linha.speaker === 'A' ? dialogo.voz_a : dialogo.voz_b;
    const langCode = voz.split('-').slice(0, 2).join('-');
    const cacheKey = `${linha.texto_en}|${voz}`;

    setPlayingIdx(idx);
    lineRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    try {
      let url;
      if (audioCache.current.has(cacheKey)) {
        url = audioCache.current.get(cacheKey);
      } else {
        const res = await fetch('/synt/text-to-speech', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ text: linha.texto_en, languageCode: langCode, voiceName: voz, pitch: 0 }),
        });
        if (!res.ok) throw new Error('TTS error');
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        audioCache.current.set(cacheKey, url);
      }

      if (stopRef.current) return;

      await new Promise(resolve => {
        const a = audioRef.current;
        a.src = url;
        a.playbackRate = speed;
        a.onended = resolve;
        a.onerror = resolve;
        a.play().catch(resolve);
      });
    } catch (e) { /* continue */ }
  }

  async function playAll() {
    if (!selected || isPlayingAll) return;
    stopRef.current = false;
    setIsPlayingAll(true);
    const snap = selected; // snapshot to avoid stale closure issues

    for (let i = 0; i < snap.linhas.length; i++) {
      if (stopRef.current) break;
      await playLine(i, snap);
      if (!stopRef.current && i < snap.linhas.length - 1) {
        await new Promise(r => setTimeout(r, 400));
      }
    }

    if (!stopRef.current) setPlayingIdx(null);
    setIsPlayingAll(false);
  }

  function toggleLinePt(idx) {
    setSelected(prev => ({
      ...prev,
      linhas: prev.linhas.map((l, i) => i === idx ? { ...l, _showPt: !l._showPt } : l),
    }));
  }

  const filtered = lista.filter(d => {
    const matchSearch = !search ||
      d.titulo.toLowerCase().includes(search.toLowerCase()) ||
      (d.topico || '').toLowerCase().includes(search.toLowerCase());
    const matchNivel = !filterNivel || d.nivel === filterNivel;
    const matchTipo  = !filterTipo  || d.par_tipo === filterTipo;
    return matchSearch && matchNivel && matchTipo;
  });

  return (
    <div className="dg-page">

      {/* ── List Panel ── */}
      <div className={`dg-list-panel${mobileView === 'dialogue' ? ' dg-mobile-hidden' : ''}`}>
        <div className="dg-list-header">
          <h2><i className="fas fa-comments" /> Diálogos em Inglês</h2>
        </div>

        <div className="dg-filters">
          <input
            placeholder="Buscar diálogos..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select value={filterNivel} onChange={e => setFilterNivel(e.target.value)}>
            <option value="">Todos os níveis</option>
            <option value="beginner">Iniciante</option>
            <option value="intermediate">Intermediário</option>
            <option value="advanced">Avançado</option>
          </select>
          <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
            <option value="">Todos os pares</option>
            <option value="hm">Homem &amp; Mulher</option>
            <option value="mm">Homem &amp; Homem</option>
            <option value="ff">Mulher &amp; Mulher</option>
          </select>
        </div>

        {loadingLista ? (
          <div className="dg-list-empty"><i className="fas fa-spinner fa-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="dg-list-empty">
            <i className="fas fa-inbox" />
            <p>Nenhum diálogo encontrado.</p>
          </div>
        ) : (
          <div className="dg-list">
            {filtered.map(d => (
              <button
                key={d.id}
                className={`dg-list-item${selected?.id === d.id ? ' active' : ''}`}
                onClick={() => selectDialogo(d.id)}
              >
                <div className="dg-item-title">{d.titulo}</div>
                <div className="dg-item-meta">
                  <span className={`dg-level ${d.nivel}`}>{NIVEL_LABELS[d.nivel]}</span>
                  <span className="dg-tipo">{PAR_LABELS[d.par_tipo]}</span>
                </div>
                {d.topico && <div className="dg-item-topico">{d.topico}</div>}
                <div className="dg-item-speakers">{d.nome_a} &amp; {d.nome_b}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Dialogue Panel ── */}
      <div className={`dg-dialogue-panel${mobileView === 'list' ? ' dg-mobile-hidden' : ''}`}>

        <button className="dg-back-btn" onClick={() => { stopAll(); setMobileView('list'); }}>
          <i className="fas fa-arrow-left" /> Voltar para lista
        </button>

        {!selected && !loadingDialogo ? (
          <div className="dg-empty-state">
            <i className="fas fa-comments" />
            <p>Selecione um diálogo para começar</p>
          </div>
        ) : loadingDialogo ? (
          <div className="dg-empty-state">
            <i className="fas fa-spinner fa-spin" />
          </div>
        ) : (
          <>
            <div className="dg-dialogue-header">
              <div className="dg-dialogue-title">{selected.titulo}</div>
              <div className="dg-dialogue-meta">
                <span className={`dg-level ${selected.nivel}`}>{NIVEL_LABELS[selected.nivel]}</span>
                {selected.topico && <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{selected.topico}</span>}
                <span>{PAR_LABELS[selected.par_tipo]}</span>
                <span>{selected.nome_a} &amp; {selected.nome_b}</span>
                <span>{selected.linhas.length} trocas</span>
              </div>
            </div>

            <div className="dg-controls">
              {isPlayingAll ? (
                <button className="dg-ctrl-btn dg-ctrl-stop" onClick={stopAll}>
                  <i className="fas fa-stop" /> Parar
                </button>
              ) : (
                <button className="dg-ctrl-btn dg-ctrl-play" onClick={playAll}>
                  <i className="fas fa-play" /> Ouvir Tudo
                </button>
              )}

              <div className="dg-speed-btns">
                {[0.75, 1, 1.25].map(s => (
                  <button
                    key={s}
                    className={`dg-speed-btn${speed === s ? ' active' : ''}`}
                    onClick={() => setSpeed(s)}
                  >
                    {s}x
                  </button>
                ))}
              </div>

              <button
                className={`dg-ctrl-btn dg-ctrl-pt${showTranslation ? ' active' : ''}`}
                onClick={() => setShowTranslation(v => !v)}
                title="Mostrar/ocultar tradução em português"
              >
                <i className="fas fa-language" /> PT
              </button>
            </div>

            <div className="dg-chat">
              {selected.linhas.map((linha, idx) => {
                const isA      = linha.speaker === 'A';
                const nome     = isA ? selected.nome_a : selected.nome_b;
                const isPlaying = playingIdx === idx;
                const showPt   = showTranslation || !!linha._showPt;

                return (
                  <div
                    key={linha.id || idx}
                    ref={el => { lineRefs.current[idx] = el; }}
                    className={`dg-bubble-row${isA ? ' dg-row-a' : ' dg-row-b'}${isPlaying ? ' playing' : ''}`}
                  >
                    <div className={`dg-avatar${isA ? ' dg-avatar-a' : ' dg-avatar-b'}`}>
                      {nome.charAt(0).toUpperCase()}
                    </div>

                    <div className="dg-bubble-content">
                      <div className="dg-bubble-name">{nome}</div>
                      <div className={`dg-bubble${isA ? ' dg-bubble-a' : ' dg-bubble-b'}`}>
                        <p className="dg-texto-en">{linha.texto_en}</p>
                        {showPt && linha.texto_pt && (
                          <p className="dg-texto-pt">{linha.texto_pt}</p>
                        )}
                        <div className="dg-bubble-actions">
                          <button
                            className={`dg-play-btn${isPlaying ? ' playing' : ''}`}
                            onClick={() => isPlaying ? stopAll() : playLine(idx)}
                            disabled={isPlayingAll && !isPlaying}
                            title={isPlaying ? 'Parar' : 'Ouvir esta linha'}
                          >
                            <i className={`fas fa-${isPlaying ? 'stop' : 'volume-up'}`} />
                          </button>
                          {linha.texto_pt && (
                            <button
                              className={`dg-pt-btn${linha._showPt ? ' active' : ''}`}
                              onClick={() => toggleLinePt(idx)}
                              title="Tradução"
                            >
                              PT
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
